import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Divider,
  Stack,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { TrendingUp, TrendingDown, AccountBalance, CalendarToday } from "@mui/icons-material";

import { arSD } from "@mui/x-data-grid/locales";

import { useGetFinancialPeriodsQuery } from "../../../redux/Slices/financial-periodsApi";
import {
  useGetTrialBalanceQuery,
  useLazyGetTrialBalanceQuery,
} from "../../../redux/Slices/trial-balanceApi";
import detectFiscalPeriod from "hooks/detectFiscalPeriod";
import MDBox from "components/MDBox";
import RTLDataGrid from "components/styled-component/RTLDataGrid";

// Summary Card Component
const SummaryCard = ({ title, value, icon, color = "primary", trend }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-SA", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 2,
    }).format(amount);
  };
};
// create Trial Balance Report
const TrialBalanceReport = () => {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const [periodId, setPeriodId] = useState(() => {
    return detectFiscalPeriod(new Date().toISOString().split("T")[0]);
  });
  // Filter Query by Fiscal Period ID, As Of Date
  const filterQuery = new URLSearchParams({
    fiscal_period_id: periodId.toString(),
    as_of_date: asOfDate,
  }).toString();
  // get Trial Balance Data
  const {
    data: incomeDataPerPeriod,
    isLoading,
    isError,
    error,
  } = useGetTrialBalanceQuery(filterQuery);
  const { data: periods, isLoading: periodsLoading } = useGetFinancialPeriodsQuery();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-EG", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // DataGrid Columns for Revenues
  const Columns = [
    {
      field: "account_code",
      headerName: "كود الحساب",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "account_name",
      headerName: "اسم الحساب",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "account_type",
      headerName: "نوع الحساب",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "debit_total",
      headerName: "المدين",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "credit_total",
      headerName: "الدائن",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "opening_balance",
      headerName: "رصيد افتتاحي",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "closing_balance",
      headerName: "رصيد نهائي",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => formatCurrency(params),
    },
    {
      field: "balance",
      headerName: "المبلغ",
      flex: 1,
      minWidth: 150,

      valueFormatter: (params) => formatCurrency(params),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          حدث خطأ أثناء تحميل البيانات: {error?.data?.message || "خطأ غير معروف"}
        </Alert>
      </Box>
    );
  }

  const reportData = incomeDataPerPeriod?.data;
  const meta = incomeDataPerPeriod?.meta;

  return (
    <>
    <MDBox p={3}>

      <Card>


      {/* Header */}
      
        <MDBox
          mx={2}
            mt={-3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
        
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              تقرير الميزان المراجعة
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {meta?.fiscal_period}
              </Typography>
              {/*  <Chip
                label={`${reportData?.period?.start_date} - ${reportData?.period?.end_date}`}
                size="small"
                variant="outlined"
              /> */}
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            تم الإنشاء في: {meta?.generated_at}
          </Typography>
        </MDBox>
      

      <Box display="flex" alignItems="center" gap={2} m={2}>
        <FormControl sx={{ width: "30%" }}>
          <InputLabel id="type">اختر الفترة </InputLabel>
          <Select
            label="اختر الفترة "
            /* error={!!errors.type} */
            name="periodId"
            value={periodId}
            onChange={(e) => {
              setPeriodId(e.target.value);
            }}
            sx={{ height: 43 }}
          >
            {periods?.data?.map((period) => (
              <MenuItem key={period.id} value={period.id}>
                {period.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl flex={1} sx={{}}>
          <TextField
            id="as-of-date"
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            sx={{}}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>
      </Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="إجمالي الإيرادات"
            value={formatCurrency(reportData?.summary?.total_revenue || 0)}
            icon={<TrendingUp color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="إجمالي المصروفات"
            value={formatCurrency(reportData?.summary?.total_expenses || 0)}
            icon={<TrendingDown color="error" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="إجمالي الربح"
            value={reportData?.summary?.gross_profit || 0}
            icon={<AccountBalance color="primary" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="صافي الدخل"
            value={reportData?.summary?.net_income >= 0 ? "success.main" : "error.main"}
            icon={<AccountBalance color="info" />}
            color={reportData?.summary?.net_income >= 0 ? "success" : "error"}
          />
        </Grid>
      </Grid>

      {/* Revenues DataGrid */}
      
        <Box sx={{ p: 2, backgroundColor: "success.light" }}>
          <Typography variant="h6" fontWeight="bold" color="success.dark">
            الإيرادات
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ height: 400, width: "100%" }}>
          <RTLDataGrid
            rows={reportData?.data || []}
            columns={Columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.account_code}
            disableRowSelectionOnClick
            
          />
        </Box>
            </Card>
            {/* Final Summary */}

        <Grid container spacing={6} mt={3}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold" color="text.main">
                إجمالي الدائنات:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.dark">
                {formatCurrency(reportData?.summary?.total_debit || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold" color="text.main">
                إجمالي المصروفات:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="error.dark">
                {formatCurrency(reportData?.summary?.total_expenses || 0)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight="bold" color="text.main">
                صافي الدخل:
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={reportData?.summary?.net_income >= 0 ? "success.main" : "error.main"}
              >
                {formatCurrency(reportData?.summary?.net_income || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
    </MDBox>

    </>
  );
};

export default TrialBalanceReport;
